import type { RequestOptions } from "@@/plugin-request/request";
import { createAppRequest, createParkRequest } from "@/services/request";

import type { PageResult } from "./types/common";
import type {
  AcceptServiceFulfillmentParams,
  AdmissionApplication,
  AdmissionApplicationListParams,
  CreateAdmissionApplicationParams,
  CreateServiceDemandParams,
  Park,
  ParkEnterpriseRelation,
  EnterpriseParkProfile,
  ServiceCatalog,
  ServiceCatalogListParams,
  ServiceDemand,
  ServiceDemandListParams,
  ServiceFulfillment,
  ServiceOrder,
  ServiceOrderListParams,
  Policy,
  PolicyListParams,
  EcosystemResource,
  EcosystemResourceListParams,
  ParkActivity,
  ParkActivityListParams,
  ParkActivityRegistrationParams,
  ActivityOps,
  ActivityOpsListParams,
  ActivityOpsRegistration,
  ActivityOpsRegistrationParams,
  EnterpriseParkAnnouncement,
  EnterpriseParkAnnouncementListParams,
  ParkLease,
  ParkLeaseListParams,
  EnergyRecord,
  EnergyRecordListParams,
  EnergySummary,
  ParkAccessApplication,
  ParkAccessApplicationListParams,
  ParkAccessApplicationPageResult,
  CreateParkAccessApplicationParams,
  EnterpriseVehicle,
  EnterpriseVehicleListParams,
  CreateEnterpriseVehicleParams,
  InvestmentProject,
  InvestmentIntent,
  IncubationProgram,
  IncubationApplication,
  Batch3AQuery,
  CreateBatch3ASubmission,
  Mentor,
  DataAuthorization,
  DataAuthorizationListParams,
  GrantDataAuthorizationParams,
  DataUsageRecord,
  DataUsageRecordListParams,
  EnterpriseGrowthArchive,
  GrowthRecommendation,
  GrowthRecommendationEnterpriseStatus,
} from "./types/park";
import type {
  FinancingDemand,
  FinancingDemandDetail,
  FinancingDemandInput,
  FinancingDemandListParams,
  FinancingMatch,
} from "./types/financing";
import type {
  EnterpriseAdmissionPark,
  IncubationParkListParams,
  IncubationParkListResult,
} from "./types/onboarding";

const request = createAppRequest();
const parkRequest = createParkRequest();

const requestByPark = <T>(
  url: string,
  parkUuid: string,
  options?: RequestOptions
) =>
  request<T>(url, {
    ...options,
    headers: {
      "x-park-uuid": parkUuid,
      ...(options?.headers || {}),
    },
  });

export const getCurrentPark = () =>
  parkRequest<Park>("/bff/park/current", {
    method: "GET",
  });

export const getMyPark = () =>
  parkRequest<{
    park: Pick<
      Park,
      | "uuid"
      | "name"
      | "type"
      | "address"
      | "contactName"
      | "contactPhone"
      | "description"
    >;
    relation: Pick<
      ParkEnterpriseRelation,
      "relationshipType" | "status" | "effectiveAt" | "endedAt"
    > & { managerName?: string };
    profile?: Pick<
      EnterpriseParkProfile,
      "contactName" | "contactPhone" | "introduction" | "incubationStage"
    >;
    latestAdmission?: Pick<
      AdmissionApplication,
      | "uuid"
      | "applicationType"
      | "status"
      | "submittedAt"
      | "reviewedAt"
      | "reviewComment"
    >;
    summary: {
      demandTotal: number;
      activeDemandTotal: number;
      orderTotal: number;
      activeOrderTotal: number;
      pendingAcceptanceFulfillmentTotal: number;
    };
  }>("/bff/enterprise/my-park", { method: "GET" });

export const listAccessibleParks = () =>
  request<Park[]>("/bff/enterprise/parks", { method: "GET" });

export const listAdmissionParks = () =>
  request<Park[]>("/bff/enterprise/admission-parks", {
    method: "GET",
  });

export const listIncubationParks = (params?: IncubationParkListParams) =>
  request<IncubationParkListResult>("/bff/enterprise/incubation/parks", {
    method: "GET",
    params,
  });

export const getAdmissionPark = (parkUuid: string) =>
  request<EnterpriseAdmissionPark>(
    `/bff/enterprise/admission-parks/${parkUuid}`,
    {
      method: "GET",
    }
  );

export const getCurrentEnterpriseRelations = () =>
  parkRequest<PageResult<ParkEnterpriseRelation>>(
    "/bff/enterprise/current/relations",
    {
      method: "GET",
    }
  );

export const getCurrentParkLeases = (params?: ParkLeaseListParams) =>
  parkRequest<PageResult<ParkLease>>("/bff/enterprise/current/leases", {
    method: "GET",
    params,
  });

export const getCurrentParkLease = (uuid: string) =>
  parkRequest<ParkLease>(`/bff/enterprise/current/leases/${uuid}`, {
    method: "GET",
  });

export const getCurrentParkLeasePlans = (params?: import("./types/park").ParkLeasePlanListParams) =>
  parkRequest<PageResult<import("./types/park").ParkLeasePlan>>(
    "/bff/enterprise/current/lease-plans",
    {
      method: "GET",
      params,
    }
  );

export const getCurrentParkLeasePlan = (uuid: string) =>
  parkRequest<import("./types/park").ParkLeasePlan>(
    `/bff/enterprise/current/lease-plans/${uuid}`,
    {
      method: "GET",
    }
  );

export const getCurrentParkLeaseRenewals = (params?: import('./types/park').ParkLeaseRenewalListParams) => parkRequest<PageResult<import('./types/park').ParkLeaseRenewal>>('/bff/enterprise/current/lease-renewals', { method: 'GET', params });
export const createCurrentParkLeaseRenewal = (data: import('./types/park').ParkLeaseRenewalRequest) => parkRequest<import('./types/park').ParkLeaseRenewal>('/bff/enterprise/current/lease-renewals', { method: 'POST', data });
export const patchCurrentParkLeaseRenewal = (uuid: string, data: Partial<import('./types/park').ParkLeaseRenewalRequest>) => parkRequest<import('./types/park').ParkLeaseRenewal>(`/bff/enterprise/current/lease-renewals/${uuid}`, { method: 'PATCH', data });
export const submitCurrentParkLeaseRenewal = (uuid: string) => parkRequest<import('./types/park').ParkLeaseRenewal>(`/bff/enterprise/current/lease-renewals/${uuid}/submit`, { method: 'POST' });
export const getCurrentParkLeaseTerminations = (params?: import('./types/park').ParkLeaseTerminationListParams) => parkRequest<PageResult<import('./types/park').ParkLeaseTermination>>('/bff/enterprise/current/lease-terminations', { method: 'GET', params });
export const getCurrentParkLeaseTermination = (uuid: string) => parkRequest<import('./types/park').ParkLeaseTermination>(`/bff/enterprise/current/lease-terminations/${uuid}`, { method: 'GET' });
export const createCurrentParkLeaseTermination = (data: import('./types/park').ParkLeaseTerminationRequest) => parkRequest<import('./types/park').ParkLeaseTermination>(`/bff/enterprise/current/leases/${data.leaseUuid}/terminations`, { method: 'POST', data });
export const patchCurrentParkLeaseTermination = (uuid: string, data: Partial<import('./types/park').ParkLeaseTerminationRequest>) => parkRequest<import('./types/park').ParkLeaseTermination>(`/bff/enterprise/current/lease-terminations/${uuid}`, { method: 'PATCH', data });
export const submitCurrentParkLeaseTermination = (uuid: string) => parkRequest<import('./types/park').ParkLeaseTermination>(`/bff/enterprise/current/lease-terminations/${uuid}/submit`, { method: 'POST' });

export const listCurrentDataAuthorizations = (
  params?: DataAuthorizationListParams
) =>
  parkRequest<PageResult<DataAuthorization>>(
    "/bff/enterprise/current/data-authorizations",
    { method: "GET", params }
  );

export const getCurrentDataAuthorization = (uuid: string) =>
  parkRequest<DataAuthorization>(
    `/bff/enterprise/current/data-authorizations/${uuid}`,
    { method: "GET" }
  );

export const grantCurrentDataAuthorization = (
  data: GrantDataAuthorizationParams
) =>
  parkRequest<DataAuthorization>(
    "/bff/enterprise/current/data-authorizations",
    { method: "POST", data }
  );

export const revokeCurrentDataAuthorization = (uuid: string) =>
  parkRequest<DataAuthorization>(
    `/bff/enterprise/current/data-authorizations/${uuid}/revoke`,
    { method: "POST" }
  );

export const listCurrentDataUsageRecords = (
  params?: DataUsageRecordListParams
) =>
  parkRequest<PageResult<DataUsageRecord>>(
    "/bff/enterprise/current/data-usage-records",
    { method: "GET", params }
  );

export const getEnterpriseAdmissionApplicationsByPark = (
  parkUuid: string,
  params?: AdmissionApplicationListParams
) =>
  requestByPark<PageResult<AdmissionApplication>>(
    "/bff/enterprise/current/admission-applications",
    parkUuid,
    {
      method: "GET",
      params,
    }
  );

export const getCurrentAdmissionApplication = (uuid: string) =>
  request<AdmissionApplication>(
    `/bff/enterprise/current/admission-applications/${uuid}`,
    {
      method: "GET",
    }
  );

export const createCurrentAdmissionApplication = (
  data: CreateAdmissionApplicationParams
) =>
  request<AdmissionApplication>(
    "/bff/enterprise/current/admission-applications",
    {
      method: "POST",
      data,
    }
  );

export const withdrawCurrentAdmissionApplication = (uuid: string) =>
  request<AdmissionApplication>(
    `/bff/enterprise/current/admission-applications/${uuid}/withdraw`,
    {
      method: "POST",
    }
  );

export const getCurrentServiceDemands = (params?: ServiceDemandListParams) =>
  parkRequest<PageResult<ServiceDemand>>(
    "/bff/enterprise/current/service-demands",
    {
      method: "GET",
      params,
    }
  );

export const getCurrentServiceDemand = (uuid: string) =>
  parkRequest<ServiceDemand>(`/bff/enterprise/current/service-demands/${uuid}`, {
    method: "GET",
  });

export const createCurrentServiceDemand = (data: CreateServiceDemandParams) =>
  parkRequest<ServiceDemand>("/bff/enterprise/current/service-demands", {
    method: "POST",
    data,
  });

export const patchCurrentServiceDemand = (
  uuid: string,
  data: Partial<
    Pick<
      ServiceDemand,
      | "title"
      | "description"
      | "category"
      | "priority"
      | "serviceCatalogUuid"
      | "dueAt"
      | "status"
    >
  >
) =>
  parkRequest<ServiceDemand>(
    `/bff/enterprise/current/service-demands/${uuid}`,
    {
      method: "PATCH",
      data,
    }
  );

export const getCurrentServiceCatalog = (params?: ServiceCatalogListParams) =>
  parkRequest<PageResult<ServiceCatalog>>(
    "/bff/enterprise/current/service-catalog",
    {
      method: "GET",
      params,
    }
  );

export const getCurrentServiceCatalogItem = (uuid: string) =>
  parkRequest<ServiceCatalog>(`/bff/enterprise/current/service-catalog/${uuid}`, {
    method: "GET",
  });

export const getCurrentServiceOrders = (params?: ServiceOrderListParams) =>
  parkRequest<PageResult<ServiceOrder>>(
    "/bff/enterprise/current/service-orders",
    {
      method: "GET",
      params,
    }
  );

export const getCurrentServiceOrder = (uuid: string) =>
  parkRequest<ServiceOrder>(`/bff/enterprise/current/service-orders/${uuid}`, {
    method: "GET",
  });

export const getCurrentServiceOrderFulfillments = (uuid: string) =>
  parkRequest<PageResult<ServiceFulfillment>>(
    `/bff/enterprise/current/service-orders/${uuid}/fulfillments`,
    {
      method: "GET",
    }
  );

export const getCurrentEnergyRecords = (params?: EnergyRecordListParams) =>
  parkRequest<PageResult<EnergyRecord>>(
    "/bff/enterprise/current/energy-records",
    {
      method: "GET",
      params,
    }
  );

export const getCurrentEnergyRecord = (uuid: string) =>
  parkRequest<EnergyRecord>(`/bff/enterprise/current/energy-records/${uuid}`, {
    method: "GET",
  });

export const getCurrentEnergySummary = (
  params?: Omit<EnergyRecordListParams, "pageNumber" | "pageSize">
) =>
  parkRequest<EnergySummary>("/bff/enterprise/current/energy-records/summary", {
    method: "GET",
    params,
  });

export const acceptCurrentServiceFulfillment = (
  uuid: string,
  data?: AcceptServiceFulfillmentParams
) =>
  parkRequest<{
    fulfillment: ServiceFulfillment;
    order: Pick<ServiceOrder, "uuid" | "status" | "completedAt">;
  }>(`/bff/enterprise/current/service-fulfillments/${uuid}/accept`, {
    method: "POST",
    data,
  });

export const listEnterpriseAnnouncements = (
  params?: EnterpriseParkAnnouncementListParams
) =>
  parkRequest<PageResult<EnterpriseParkAnnouncement>>(
    "/bff/enterprise/announcements",
    {
      method: "GET",
      params,
    }
  );

export const getEnterpriseAnnouncement = (uuid: string) =>
  parkRequest<EnterpriseParkAnnouncement>(
    `/bff/enterprise/announcements/${uuid}`,
    {
      method: "GET",
    }
  );

export const recordEnterpriseAnnouncementRead = (uuid: string) =>
  parkRequest<{ firstRead: boolean; readCount: number }>(
    `/bff/enterprise/announcements/${uuid}/read`,
    { method: "POST" }
  );

export const listEnterprisePolicies = (params?: PolicyListParams) =>
  parkRequest<PageResult<Policy>>("/bff/enterprise/policies", {
    method: "GET",
    params,
  });

export const getEnterprisePolicy = (uuid: string) =>
  parkRequest<Policy>(`/bff/enterprise/policies/${uuid}`, {
    method: "GET",
  });

export const listEnterpriseEcosystemResources = (
  params?: EcosystemResourceListParams
) =>
  parkRequest<PageResult<EcosystemResource>>(
    "/bff/enterprise/ecosystem-resources",
    {
      method: "GET",
      params,
    }
  );

export const listEnterpriseMentors = (params?: {
  keyword?: string;
  pageNumber?: number;
  pageSize?: number;
}) =>
  parkRequest<{ items: Mentor[]; pageNumber: number; pageSize: number; total: number }>(
    '/bff/enterprise/mentors',
    { method: 'GET', params },
  );

export const getEnterpriseMentor = (uuid: string) =>
  parkRequest<Mentor>(`/bff/enterprise/mentors/${uuid}`, { method: 'GET' });

export const getEnterpriseEcosystemResource = (uuid: string) =>
  parkRequest<EcosystemResource>(
    `/bff/enterprise/ecosystem-resources/${uuid}`,
    {
      method: "GET",
    }
  );

export const listCurrentFinancingDemands = (
  params?: FinancingDemandListParams
) =>
  parkRequest<{
    items: FinancingDemand[];
    pageNumber: number;
    pageSize: number;
    total: number;
  }>("/bff/enterprise/current/financing-demands", { method: "GET", params });

export const getCurrentFinancingDemand = (uuid: string) =>
  parkRequest<FinancingDemandDetail>(
    `/bff/enterprise/current/financing-demands/${uuid}`,
    { method: "GET" }
  );

export const createCurrentFinancingDemand = (
  data: Partial<FinancingDemandInput>
) =>
  parkRequest<FinancingDemand>("/bff/enterprise/current/financing-demands", {
    method: "POST",
    data,
  });

export const updateCurrentFinancingDemand = (
  uuid: string,
  data: Partial<FinancingDemandInput>
) =>
  parkRequest<FinancingDemand>(
    `/bff/enterprise/current/financing-demands/${uuid}`,
    { method: "PATCH", data }
  );

export const deleteCurrentFinancingDemand = (uuid: string) =>
  parkRequest<{ uuid: string }>(
    `/bff/enterprise/current/financing-demands/${uuid}`,
    { method: "DELETE" }
  );

export const submitCurrentFinancingDemand = (uuid: string) =>
  parkRequest<FinancingDemand>(
    `/bff/enterprise/current/financing-demands/${uuid}/submit`,
    { method: "POST" }
  );

export const listCurrentFinancingMatches = (uuid: string) =>
  parkRequest<FinancingMatch[]>(
    `/bff/enterprise/current/financing-demands/${uuid}/matches`,
    { method: "GET" }
  );

export const getCurrentActivities = (params?: ParkActivityListParams) =>
  parkRequest<PageResult<ParkActivity>>("/bff/enterprise/activities", {
    method: "GET",
    params,
  });

export const getCurrentActivity = (uuid: string) =>
  parkRequest<ParkActivity>(`/bff/enterprise/activities/${uuid}`, {
    method: "GET",
  });

export const registerCurrentActivity = (
  uuid: string,
  data: ParkActivityRegistrationParams
) =>
  parkRequest(`/bff/enterprise/activities/${uuid}/register`, {
    method: "POST",
    data,
  });

export const cancelCurrentActivityRegistration = (uuid: string) =>
  parkRequest(`/bff/enterprise/activities/${uuid}/cancel-registration`, {
    method: "POST",
  });

export const listEnterpriseActivityOps = (params?: ActivityOpsListParams) =>
  parkRequest<PageResult<ActivityOps>>('/bff/enterprise/activity-ops', { method: 'GET', params });
export const listMyActivityOps = (params?: ActivityOpsListParams) =>
  parkRequest<PageResult<ActivityOpsRegistration & { activity: ActivityOps; participants: ActivityOpsRegistration['participants'] }>>('/bff/enterprise/activity-ops/mine', { method: 'GET', params });
export const getEnterpriseActivityOps = (uuid: string) =>
  parkRequest<ActivityOps & { registration?: ActivityOpsRegistration | null }>('/bff/enterprise/activity-ops/' + uuid, { method: 'GET' });
export const getMyActivityOpsRegistration = (activityUuid: string, registrationUuid: string) =>
  parkRequest<ActivityOpsRegistration & { activity: ActivityOps; participants: NonNullable<ActivityOpsRegistration['participants']> }>(`/bff/enterprise/activity-ops/${activityUuid}/registrations/${registrationUuid}`, { method: 'GET' });
export const registerEnterpriseActivityOps = (uuid: string, data: ActivityOpsRegistrationParams) =>
  parkRequest<ActivityOpsRegistration>(`/bff/enterprise/activity-ops/${uuid}/registrations`, { method: 'POST', data });
export const cancelEnterpriseActivityOps = (uuid: string, registrationUuid: string) =>
  parkRequest<ActivityOpsRegistration>(`/bff/enterprise/activity-ops/${uuid}/registrations/${registrationUuid}/cancel`, { method: 'POST' });

export const listCurrentAccessApplications = (
  params?: ParkAccessApplicationListParams
) =>
  parkRequest<ParkAccessApplicationPageResult>(
    "/bff/enterprise/access-applications",
    { method: "GET", params }
  );

export const getCurrentAccessApplication = (uuid: string) =>
  parkRequest<ParkAccessApplication>(
    `/bff/enterprise/access-applications/${uuid}`,
    { method: "GET" }
  );

export const createCurrentAccessApplication = (
  data: CreateParkAccessApplicationParams
) =>
  parkRequest<ParkAccessApplication>("/bff/enterprise/access-applications", {
    method: "POST",
    data,
  });

export const cancelCurrentAccessApplication = (uuid: string) =>
  parkRequest<ParkAccessApplication>(
    `/bff/enterprise/access-applications/${uuid}/cancel`,
    { method: "POST" }
  );

export const listCurrentVehicles = (params?: EnterpriseVehicleListParams) =>
  parkRequest<{
    items: EnterpriseVehicle[];
    pageNumber: number;
    pageSize: number;
    total: number;
  }>("/bff/enterprise/vehicles", { method: "GET", params });

export const getCurrentVehicle = (uuid: string) =>
  parkRequest<EnterpriseVehicle>(`/bff/enterprise/vehicles/${uuid}`, {
    method: "GET",
  });

export const createCurrentVehicle = (data: CreateEnterpriseVehicleParams) =>
  parkRequest<EnterpriseVehicle>("/bff/enterprise/vehicles", {
    method: "POST",
    data,
  });

export const updateCurrentVehicle = (
  uuid: string,
  data: CreateEnterpriseVehicleParams
) =>
  parkRequest<EnterpriseVehicle>(`/bff/enterprise/vehicles/${uuid}`, {
    method: "PATCH",
    data,
  });

export const deleteCurrentVehicle = (uuid: string) =>
  parkRequest<{ uuid: string }>(`/bff/enterprise/vehicles/${uuid}`, {
    method: "DELETE",
  });

export const disableCurrentVehicle = (uuid: string) =>
  parkRequest<EnterpriseVehicle>(`/bff/enterprise/vehicles/${uuid}/disable`, {
    method: "POST",
  });

export const listCurrentInvestmentProjects = (params?: Batch3AQuery) =>
  parkRequest<{
    items: InvestmentProject[];
    pageNumber: number;
    pageSize: number;
    total: number;
  }>("/bff/enterprise/investment-projects", {
    method: "GET",
    params,
  });
export const getCurrentInvestmentProject = (uuid: string) =>
  parkRequest<InvestmentProject & { intent?: InvestmentIntent | null }>(
    `/bff/enterprise/investment-projects/${uuid}`,
    { method: "GET" }
  );
export const createCurrentInvestmentIntent = (
  projectUuid: string,
  data: CreateBatch3ASubmission
) =>
  parkRequest<InvestmentIntent>(
    `/bff/enterprise/investment-projects/${projectUuid}/intents`,
    { method: "POST", data }
  );
export const listCurrentInvestmentIntents = (params?: Batch3AQuery) =>
  parkRequest<{
    items: InvestmentIntent[];
    pageNumber: number;
    pageSize: number;
    total: number;
  }>("/bff/enterprise/investment-intents", {
    method: "GET",
    params,
  });
export const getCurrentInvestmentIntent = (uuid: string) =>
  parkRequest<InvestmentIntent>(`/bff/enterprise/investment-intents/${uuid}`, {
    method: "GET",
  });
export const listCurrentIncubationPrograms = (params?: Batch3AQuery) =>
  parkRequest<{
    items: IncubationProgram[];
    pageNumber: number;
    pageSize: number;
    total: number;
  }>("/bff/enterprise/incubation-programs", {
    method: "GET",
    params,
  });
export const getCurrentIncubationProgram = (uuid: string) =>
  parkRequest<IncubationProgram>(
    `/bff/enterprise/incubation-programs/${uuid}`,
    { method: "GET" }
  );
export const createCurrentIncubationApplication = (
  programUuid: string,
  data: CreateBatch3ASubmission
) =>
  parkRequest<IncubationApplication>(
    `/bff/enterprise/incubation-programs/${programUuid}/applications`,
    { method: "POST", data }
  );
export const listCurrentIncubationApplications = (params?: Batch3AQuery) =>
  parkRequest<{
    items: IncubationApplication[];
    pageNumber: number;
    pageSize: number;
    total: number;
  }>("/bff/enterprise/incubation-applications", {
    method: "GET",
    params,
  });
export const getCurrentIncubationApplication = (uuid: string) =>
  parkRequest<IncubationApplication>(
    `/bff/enterprise/incubation-applications/${uuid}`,
    { method: "GET" }
  );
export const getCurrentGrowthProfile = () =>
  parkRequest<EnterpriseGrowthArchive>("/bff/enterprise/current/growth-profile", {
    method: "GET",
  });

export const listCurrentGrowthRecommendations = (params?: {
  targetType?: string;
  status?: string;
  pageNumber?: number;
  pageSize?: number;
}) =>
  parkRequest<PageResult<GrowthRecommendation>>(
    "/bff/enterprise/growth/recommendations",
    { method: "GET", params },
  );

export const updateCurrentGrowthRecommendationStatus = (
  uuid: string,
  status: GrowthRecommendationEnterpriseStatus,
) =>
  parkRequest<GrowthRecommendation>(
    `/bff/enterprise/growth/recommendations/${uuid}/status`,
    { method: "PUT", data: { status } },
  );

export const listMyEnterpriseEntries = (params?: any) =>
  parkRequest<PageResult<any>>("/bff/enterprise/my-entries", {
    method: "GET",
    params,
  });

export const getMyEnterpriseEntry = (uuid: string) =>
  parkRequest<any>(`/bff/enterprise/my-entries/${uuid}`, {
    method: "GET",
  });
